import React, { useMemo, useCallback } from 'react';
import { DateTime, Duration } from 'luxon';
import StartTimeIcon from '@mui/icons-material/Timer';
import EndTimeIcon from '@mui/icons-material/AlarmOn';
import makeStyles from '@mui/styles/makeStyles';
import { useWatch, useFormContext } from 'react-hook-form';
import { CalendarDateWithToday } from 'modules/common/components/CalendarDate';
import { TimeInputField } from 'modules/common/components/TimeInputField';
import { useMessages } from 'modules/intl';
import { useTimePickerProps } from 'modules/notes/common';
import { TimeDurationControl } from 'modules/common/components/TimeDurationControl';
import { SplitEntry, SPLIT_NOTE_FIELDS } from '../../types';
import { useTimeHandlers } from '../../hooks/useTimeHandlers';
import { useSplitEntriesContext } from '../../SplitEntriesProvider';

type DateTimeContentProps = {
  entry: SplitEntry;
  position: number;
};
export const messagesKeyMap = {
  startTime: 'note.startTime',
  endTime: 'note.endTime',
  duration: 'note.duration'
};
const startTimeIcon = <StartTimeIcon color="action" />;
const endTimeIcon = <EndTimeIcon color="action" />;

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(-1.5),
    paddingBottom: theme.spacing(1)
  },
  startEndTimeInput: {
    maxWidth: theme.spacing(20),
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    '& .MuiFormHelperText-contained': {
      margin: theme.spacing(0)
    },
    '& .MuiInputAdornment-positionEnd': {
      marginLeft: theme.spacing(0.5)
    }
  },
  duration: {
    marginLeft: theme.spacing(0.5)
  }
}));

const messageKeys = [
  'note.error.startTimeRequired',
  'note.error.endTimeRequired',
  'note.error.endTimeShouldBeGreaterThanStartTime',
  'note.error.startAndEndTimeShouldNotBeEqual',
  'note.error.durationRequired',
  'note.error.durationShouldBeInStartTimeEndTimeRange'
];
export const DateTimeContent: React.FC<DateTimeContentProps> = ({
  entry,
  position
}: DateTimeContentProps) => {
  const classes = useStyles();
  const {
    formState: { errors }
  } = useFormContext();
  const errorMessages = useMessages(messageKeys);
  const messages = useMessages(Object.values(messagesKeyMap));
  const startTimeName = `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.START_TIME}`;
  const endTimeName = `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.END_TIME}`;
  const durationName = `${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}.${position}.${SPLIT_NOTE_FIELDS.DURATION}`;

  const { splitEntries, showStartEndTime } = useSplitEntriesContext();
  const startTime = useMemo(() => splitEntries[position].startTime, [
    position,
    splitEntries
  ]);
  const endTime = useMemo(() => splitEntries[position].endTime, [
    position,
    splitEntries
  ]);
  const durationInSec = useWatch({
    name: durationName,
    defaultValue: entry.duration
  });

  const {
    durationChangeHandler,
    endTimeChangeHandler,
    startTimeChangeHandler
  } = useTimeHandlers({
    date: entry.date,
    position: position,
    startTime,
    endTime
  });

  const duration = useMemo(
    () => Duration.fromObject({ seconds: durationInSec ?? 0 }),
    [durationInSec]
  );

  const valueProps = useMemo(
    () => (durationInSec === undefined ? { value: undefined } : {}),
    [durationInSec]
  );

  const timePickerProps = useTimePickerProps();

  const errorMessage = useCallback(
    (
      type:
        | SPLIT_NOTE_FIELDS.START_TIME
        | SPLIT_NOTE_FIELDS.END_TIME
        | SPLIT_NOTE_FIELDS.DURATION
    ) => {
      const key =
        (errors &&
          errors[`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`] &&
          errors[`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`][position] &&
          Object.keys(
            errors[`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`][position]
          ).some((key) => key === type) &&
          errors[`${SPLIT_NOTE_FIELDS.SPLIT_ENTRIES}`][position][`${type}`]
            ?.message) ??
        '';
      return errorMessages[key];
    },
    [errors, position, errorMessages]
  );

  const timeErrorProps = useCallback(
    (
      key:
        | SPLIT_NOTE_FIELDS.START_TIME
        | SPLIT_NOTE_FIELDS.END_TIME
        | SPLIT_NOTE_FIELDS.DURATION
    ) => {
      const errorText = errorMessage(key);
      return errorText
        ? {
            error: true,
            helperText: errorText
          }
        : {};
    },
    [errorMessage]
  );

  return (
    <div
      className={classes.root}
      data-testid={`split-note-date-root-${position}`}
    >
      <CalendarDateWithToday
        testId={`split-note-date-${position}`}
        hasEndAdornment
        value={DateTime.fromJSDate(entry.date)}
        inputVariant="outlined"
        handleDateChange={() => {}}
        fitContent
        disabled
      />
      {showStartEndTime && (
        <>
          <TimeInputField
            testId={`split-note-startTime-${position}`}
            name={startTimeName}
            label={messages[messagesKeyMap.startTime]}
            clockMode={timePickerProps.ampm ? 12 : 24}
            value={startTime ? DateTime.fromJSDate(startTime) : null}
            onChange={startTimeChangeHandler}
            className={classes.startEndTimeInput}
            icon={startTimeIcon}
            {...timeErrorProps(SPLIT_NOTE_FIELDS.START_TIME)}
          />
          <TimeInputField
            testId={`split-note-endTime-${position}`}
            name={endTimeName}
            label={messages[messagesKeyMap.endTime]}
            clockMode={timePickerProps.ampm ? 12 : 24}
            value={endTime ? DateTime.fromJSDate(endTime) : null}
            onChange={endTimeChangeHandler}
            className={classes.startEndTimeInput}
            icon={endTimeIcon}
            {...timeErrorProps(SPLIT_NOTE_FIELDS.END_TIME)}
          />
        </>
      )}
      <TimeDurationControl
        testId={`split-note-duration-${position}`}
        className={classes.duration}
        running={false}
        timer={duration}
        duration={durationInSec}
        onEnter={durationChangeHandler}
        {...timeErrorProps(SPLIT_NOTE_FIELDS.DURATION)}
        {...valueProps}
        size="small"
      />
    </div>
  );
};
